import dayjs, { Dayjs } from 'dayjs';

import { SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import { CustomTableQuickFilter } from '@/components/table/quickFilters/CustomTableQuickFilter.model.ts';

export const startOfToday = (): Dayjs => dayjs().startOf('day');

export const daysAgo = (days: number): Dayjs => startOfToday().subtract(days, 'day');

export const isSameDay = (value: unknown, day: Dayjs): boolean =>
  dayjs.isDayjs(value) && value.isValid() && value.isSame(day, 'day');

export const isEmptyDate = (value: unknown): boolean =>
  value === null || value === undefined || (dayjs.isDayjs(value) && !value.isValid());

export const isOption = <T extends boolean | number | string>(
  option: Partial<SelectSearchOptionModel<T>> | null | undefined,
  value: T,
): boolean => option?.value === value;

export const hasOnlyOptions = <T extends boolean | number | string>(
  options: Partial<SelectSearchOptionModel<T>>[] | null | undefined,
  values: T[],
): boolean =>
  (options ?? []).length === values.length &&
  values.every(value => (options ?? []).some(option => option?.value === value));

export const hasOption = <T extends boolean | number | string>(
  options: Partial<SelectSearchOptionModel<T>>[] | null | undefined,
  value: T,
): boolean => (options ?? []).some(option => option?.value === value);

export const withOption = <T extends boolean | number | string>(
  options: SelectSearchOptionModel<T>[] | null | undefined,
  option: SelectSearchOptionModel<T>,
): SelectSearchOptionModel<T>[] =>
  hasOption(options, option.value) ? [...(options ?? [])] : [...(options ?? []), option];

export const withoutOption = <T extends boolean | number | string>(
  options: SelectSearchOptionModel<T>[] | null | undefined,
  value: T,
): SelectSearchOptionModel<T>[] => (options ?? []).filter(option => option.value !== value);

export const findOption = <T extends boolean | number | string>(
  options: SelectSearchOptionModel<T>[],
  value: T,
): SelectSearchOptionModel<T> =>
  options.find(option => option.value === value) ?? { label: String(value), value };

type OptionsKey<MODEL extends object> = {
  [KEY in keyof MODEL]: MODEL[KEY] extends SelectSearchOptionModel<number>[] ? KEY : never;
}[keyof MODEL];

export const dictionaryQuickFilters = <MODEL extends object>(
  options: SelectSearchOptionModel<number>[],
  key: OptionsKey<MODEL>,
): CustomTableQuickFilter<MODEL>[] => {
  const selected = (params: MODEL) => params[key] as SelectSearchOptionModel<number>[];
  return options.map(option => ({
    apply: params => ({ [key]: withOption(selected(params), option) }) as Partial<MODEL>,
    clear: params => ({ [key]: withoutOption(selected(params), option.value) }) as Partial<MODEL>,
    id: `${String(key)}-${option.value}`,
    isActive: params => hasOption(selected(params), option.value),
    label: option.label,
  }));
};

export const resolveQuickFilterToggle = <MODEL extends object>(
  filter: CustomTableQuickFilter<MODEL>,
  filters: CustomTableQuickFilter<MODEL>[],
  current: MODEL,
  isActive: boolean,
): Partial<MODEL> => {
  let next = current;
  const changes: Partial<MODEL> = {};
  const merge = (patch: Partial<MODEL>) => {
    Object.assign(changes, patch);
    next = { ...next, ...patch };
  };

  if (isActive) {
    merge(filter.clear(next));
    return changes;
  }

  filters
    .filter(other => other.id !== filter.id && other.group && other.group === filter.group)
    .forEach(other => {
      if (other.isActive(next)) {
        merge(other.clear(next));
      }
    });
  merge(filter.apply(next));
  return changes;
};
